import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { sanitizeHtml } from "@/lib/sanitize";
import { toast } from "sonner";
import { fmtAmount, equivalentFor } from "@/lib/currency";
import { EngagementBreakdown } from "@/components/EngagementBreakdown";
import { FxDisclaimer } from "@/components/shared/DualPrice";
import { SignaturePad } from "@/components/SignaturePad";

export default function PortalEngagement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile, refresh } = usePortalAuth();
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agree, setAgree] = useState(false);
  const [sig, setSig] = useState({ method: "drawn", drawnImage: null, typedName: "" });
  const [busy, setBusy] = useState(false);
  const [signedDocId, setSignedDocId] = useState(null);
  const [cc, setCc] = useState({ usd_thb_rate: 33, show_thb: false });

  const load = useCallback(async () => {
    try {
      const { data } = await portalApi.get(`/portal/engagements/${id}`);
      setEng(data);
      setSig((s) => ({ ...s, typedName: profile?.full_name || "" }));
      if (data.signed) setSignedDocId(data.signed_document_id);
    } catch { setError("This engagement letter is unavailable."); }
    finally { setLoading(false); }
  }, [id, profile]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { portalApi.get("/portal/currency-context").then((r) => r?.data && setCc(r.data)).catch(() => {}); }, []);

  const sigValid = sig.method === "drawn" ? !!sig.drawnImage : !!sig.typedName.trim();

  const sign = async () => {
    if (!agree) return toast.error("Please confirm you agree to the terms.");
    if (!sigValid) return toast.error(sig.method === "drawn" ? "Please draw your signature." : "Please type your full legal name.");
    setBusy(true);
    try {
      const payload = {
        agree: true,
        typed_signature: sig.method === "typed" ? sig.typedName.trim() : (profile?.full_name || ""),
        signature_method: sig.method === "drawn" ? "drawn" : "typed",
        signature_image_base64: sig.method === "drawn" ? sig.drawnImage : null,
      };
      const { data } = await portalApi.post(`/portal/engagements/${id}/sign`, payload);
      setSignedDocId(data.signed_document_id);
      setEng((e) => ({ ...e, signed: true, status: "signed" }));
      toast.success("Engagement letter signed — welcome aboard!");
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sign"); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const res = await portalApi.get(`/portal/documents/${signedDocId}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${eng.engagement_number}-signed.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the signed letter."); }
  };

  if (loading) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;
  if (error) return <div className="text-center py-20 text-slate-500"><Icons.AlertTriangle className="h-8 w-8 text-[#F5A623] mx-auto mb-2" />{error}</div>;

  const lifecycle = eng.lifecycle || [];
  const signedDate = (eng.executed?.signed_at || eng.signed_at || "").slice(0, 10);
  const pricing = eng.pricing || {};
  const pcur = pricing.currency || "USD";
  const eq = (v) => equivalentFor(v, pcur, { rate: cc.usd_thb_rate, showThb: cc.show_thb });
  const hasFees = pricing.total != null || (eng.engagement_breakdown && Object.keys(eng.engagement_breakdown).length > 0);

  return (
    <div className="max-w-3xl mx-auto" data-testid="portal-engagement">
      <button onClick={() => navigate("/portal")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#171B2E] mb-4">
        <Icons.ArrowLeft className="h-4 w-4" />Back to dashboard
      </button>

      {lifecycle.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 mb-4" data-testid="portal-engagement-lifecycle">
          <div className="flex items-center justify-between">
            {lifecycle.map((s, i) => (
              <div key={s.key} className="flex items-center flex-1 last:flex-none" data-testid={`portal-lifecycle-stage-${s.key}`}>
                <div className="flex flex-col items-center">
                  <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                    s.state === "complete" ? "bg-emerald-500 border-emerald-500 text-white"
                    : s.state === "current" ? "bg-[#F5A623] border-[#F5A623] text-[#171B2E]"
                    : "bg-white border-slate-300 text-slate-400"}`}>
                    {s.state === "complete" ? <Icons.Check className="h-4 w-4" /> : i + 1}
                  </div>
                  <span className={`mt-1.5 text-[11px] font-medium ${s.state === "pending" ? "text-slate-400" : "text-[#171B2E]"}`}>{s.label}</span>
                </div>
                {i < lifecycle.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1.5 -mt-4 ${lifecycle[i + 1].state !== "pending" ? "bg-emerald-400" : "bg-slate-200"}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#171B2E] text-white px-6 py-5">
          <div className="text-xs text-[#F5A623] font-mono">{eng.engagement_number}</div>
          <h1 className="font-heading text-2xl font-bold mt-0.5">{eng.title || "Engagement Letter"}</h1>
        </div>
        <div className="p-6">
          <div className="border border-slate-100 rounded-lg p-5 bg-slate-50/50 text-sm text-slate-700 leading-relaxed max-h-[48vh] overflow-y-auto tf-scroll"
            data-testid="portal-engagement-body" dangerouslySetInnerHTML={{ __html: sanitizeHtml(eng.letter_body) }} />

          {hasFees && (
            <div className="mt-5 border border-slate-200 rounded-xl p-4" data-testid="portal-engagement-fees">
              <h3 className="text-xs uppercase tracking-wide text-slate-400 mb-2">Engagement Fees</h3>
              {pricing.total != null && (
                <div className="flex flex-col items-end gap-1 text-sm">
                  {pricing.subtotal != null && <div className="text-slate-500">Subtotal <span className="font-medium text-slate-800 ml-3">{fmtAmount(pricing.subtotal, pcur)}</span>{eq(pricing.subtotal) && <span className="text-[11px] text-slate-400 ml-1">≈ {eq(pricing.subtotal)}</span>}</div>}
                  {pricing.discount > 0 && <div className="text-slate-500">Discount <span className="font-medium text-rose-600 ml-3">-{fmtAmount(pricing.discount, pcur)}</span></div>}
                  <div className="text-lg font-semibold text-[#171B2E]">Total <span className="ml-3">{fmtAmount(pricing.total, pcur)}</span>{eq(pricing.total) && <span className="text-[11px] text-slate-400 ml-2 font-normal">≈ {eq(pricing.total)}</span>}</div>
                </div>
              )}
              <EngagementBreakdown breakdown={eng.engagement_breakdown} rate={cc.usd_thb_rate} showThb={cc.show_thb} />
              {(eq(pricing.total) || cc.show_thb) && !(eng.engagement_breakdown && Object.keys(eng.engagement_breakdown).length > 0) && <FxDisclaimer className="mt-2 text-right" />}
            </div>
          )}

          {eng.signed ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center mt-5" data-testid="portal-engagement-signed">
              <Icons.CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
              <div className="font-semibold text-emerald-800" data-testid="portal-engagement-executed-heading">Engagement Letter Executed</div>
              {signedDate && <p className="text-sm text-emerald-700 mt-0.5" data-testid="portal-engagement-signed-date">Signed on {signedDate}. Your team will begin your onboarding shortly.</p>}
              {signedDocId && (
                <button onClick={download} data-testid="portal-engagement-download"
                  className="mt-4 inline-flex items-center gap-2 bg-[#171B2E] hover:bg-[#222842] text-white text-sm rounded-lg px-4 py-2">
                  <Icons.Download className="h-4 w-4 text-[#F5A623]" />Download signed copy
                </button>
              )}
            </div>
          ) : (
            <div className="border-t border-slate-100 pt-5 mt-5" data-testid="portal-sign-section">
              <SignaturePad
                onChange={setSig}
                legalName={profile?.full_name || ""}
                label="Your Electronic Signature"
              />

              <label className="flex items-start gap-2.5 text-sm text-slate-700 bg-slate-50 rounded-lg p-3.5 mt-4 mb-4">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
                  className="mt-0.5 accent-[#F5A623]" data-testid="portal-sign-agree" />
                <span className="font-medium">I have reviewed and agree to this Engagement Letter and the incorporated Master Engagement Terms and Conditions. I intend my signature above as my legally binding electronic signature.</span>
              </label>

              <button onClick={sign} disabled={busy || !sigValid || !agree} data-testid="portal-sign-btn"
                className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-50 text-[#171B2E] font-semibold rounded-lg py-3 flex items-center justify-center gap-2 transition-opacity">
                {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.PenTool className="h-4 w-4" />}
                Sign Engagement Letter
              </button>
            </div>
          )}
        </div>
      </div>
      <p className="text-center text-xs text-slate-400 mt-4">Secured by Tax Flow · Expat Tax Firm</p>
    </div>
  );
}
